import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customersApi, invoicesApi, type Customer, type CustomerInput } from "../lib/business";
import { viewFileInNewTab, downloadFile } from "../lib/api";
import { useLangStore } from "../store/lang";
import { t } from "../i18n/strings";
import { todayLocalISO } from "../lib/dates";

export function CustomersPage() {
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Customer | null>(null);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["customers", q],
    queryFn: () => customersApi.list(q),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["customers"] });
  }

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Customers</div>
          <h1 className="stage-title">
            People you <em>invoice.</em>
          </h1>
          <p className="stage-sub">
            One record per customer. Used on invoice and payment reminders.
          </p>
        </div>
      </div>

      <div className="detail-2col" style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 24, alignItems: "start" }}>
        <div className="card card-pad-lg">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <input
              className="form-input"
              style={{ maxWidth: 280 }}
              placeholder="Search by name or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button
              className="btn btn-line"
              style={{ fontSize: 12, padding: "8px 14px" }}
              onClick={() => setEditing({} as Customer)}
            >
              + New customer
            </button>
          </div>

          {isLoading ? (
            <div style={{ color: "var(--ink-3)" }}>Loading…</div>
          ) : (data?.customers.length ?? 0) === 0 ? (
            <div
              className="card"
              style={{
                padding: "40px 24px",
                textAlign: "center",
                color: "var(--ink-3)",
                background: "var(--paper-2)",
                borderStyle: "dashed",
              }}
            >
              No customers yet. Add your first one to start invoicing.
            </div>
          ) : (
            <div className="table-cards table-scroll">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Name</th>
                  <th style={th}>Contact</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {data?.customers.map((c) => {
                  const initials = c.name
                    .split(/\s+/)
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();
                  return (
                    <tr key={c.id}>
                      <td style={td} data-fullwidth data-label="Name">
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <div className="avatar av-2">{initials}</div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{c.name}</div>
                            {c.billingAddress && (
                              <div style={{ fontSize: 12, color: "var(--ink-4)" }}>
                                {c.billingAddress}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={td} data-label="Contact">
                        <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                          {c.email ?? "—"}
                          <br />
                          {c.phone ?? ""}
                        </div>
                      </td>
                      <td style={{ ...td, textAlign: "right" }} data-fullwidth>
                        <div style={{ display: "inline-flex", gap: 4 }}>
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "6px 8px", fontSize: 11 }}
                            onClick={() => { setEditing(c); window.location.hash = "outstanding"; }}
                            title="See unpaid invoices for this customer"
                          >
                            Outstanding
                          </button>
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "6px 8px", fontSize: 11 }}
                            onClick={() => { setEditing(c); window.location.hash = "statement"; }}
                            title="Download statement of account for a date range"
                          >
                            Statement
                          </button>
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "6px 10px", fontSize: 12 }}
                            onClick={() => setEditing(c)}
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* `key` forces a remount on every editing change so the draft state
            resets — same fix as the supplier form (Shamil 2026-06-16). */}
        <CustomerForm
          key={editing ? (editing.id ?? "new") : "idle"}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      </div>
    </>
  );
}

// SL VAT registration numbers are `<9-digit TIN>-7000` (and similar branch
// suffixes). Strip the trailing -NNNN to recover the TIN.
function vatToTin(vat: string | null | undefined): string | null {
  const v = (vat || "").trim();
  if (!v) return null;
  const m = v.match(/^(.+)-\d+\s*$/);
  return m ? m[1].trim() : null;
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 0",
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  fontWeight: 500,
  borderBottom: "0.5px solid var(--line)",
};
const td: React.CSSProperties = {
  padding: "12px 0",
  fontSize: 13,
  borderBottom: "0.5px solid var(--line)",
};

function CustomerForm({
  editing,
  onClose,
  onSaved,
}: {
  editing: Customer | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const blank: CustomerInput = {
    name: "",
    email: "",
    phone: "",
    billingAddress: "",
    vatRegistrationNo: "",
    tin: "",
    isExport: false,
  };
  const initial: CustomerInput = editing
    ? {
        name: editing.name ?? "",
        email: editing.email ?? "",
        phone: editing.phone ?? "",
        billingAddress: editing.billingAddress ?? "",
        vatRegistrationNo: editing.vatRegistrationNo ?? "",
        tin: editing.tin ?? "",
        isExport: editing.isExport ?? false,
      }
    : blank;

  const { lang } = useLangStore();
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // When the user clicks "Outstanding" / "Statement" from the row, we set a
  // URL hash. The form is re-keyed so this effect runs on mount with the
  // hash already set; scroll the matching panel into view (delay so the
  // panels have rendered).
  useEffect(() => {
    if (!editing?.id) return;
    const target = window.location.hash.replace("#", "");
    if (target !== "outstanding" && target !== "statement") return;
    const tick = window.setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
      // Clear the hash so a subsequent Edit click doesn't re-scroll.
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }, 200);
    return () => window.clearTimeout(tick);
  }, [editing?.id]);

  if (!editing) {
    return (
      <div className="card card-pad-lg" style={{ background: "var(--paper-2)" }}>
        <div className="stage-eyebrow">New customer</div>
        <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 8 }}>
          Click "+ New customer" to add someone, or pick a row to edit.
        </p>
      </div>
    );
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    // Client-side VAT→TIN guard (matches the backend rule).
    if ((draft.vatRegistrationNo || "").trim() && !(draft.tin || "").trim()) {
      setErr("TIN is required when a VAT registration no. is set. The TIN is the digits before the -7000 suffix.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const body = {
        name: draft.name,
        email: draft.email || null,
        phone: draft.phone || null,
        billingAddress: draft.billingAddress || null,
        vatRegistrationNo: draft.vatRegistrationNo || null,
        tin: draft.tin || null,
        isExport: draft.isExport ?? false,
      };
      if (editing!.id) await customersApi.update(editing!.id, body);
      else await customersApi.create(body);
      onSaved();
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      let parsed: { error?: string; field?: string; message?: string } | null = null;
      try { parsed = JSON.parse(raw); } catch { /* not JSON */ }
      if (parsed?.error === "duplicate_tax_id" || parsed?.error === "tin_required") {
        setErr(parsed.message ?? "Could not save.");
      } else {
        setErr("Could not save. Check the details.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card card-pad-lg form-side-panel" style={{ position: "sticky", top: 80 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div className="stage-eyebrow">{editing.id ? "Edit" : "New"}</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500 }}>
            {editing.id ? editing.name : "Add customer"}
          </div>
        </div>
        <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 18 }} onClick={onClose}>
          ×
        </button>
      </div>
      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} required />
        <Field label="Email" type="email" value={draft.email ?? ""} onChange={(v) => setDraft({ ...draft, email: v })} />
        <Field label="Phone (WhatsApp)" value={draft.phone ?? ""} onChange={(v) => setDraft({ ...draft, phone: v })} />
        <Field
          label="Billing address"
          value={draft.billingAddress ?? ""}
          onChange={(v) => setDraft({ ...draft, billingAddress: v })}
        />
        <div className="form-field">
          <label className="form-label">VAT registration no.</label>
          <input
            className="form-input"
            value={draft.vatRegistrationNo ?? ""}
            placeholder="123456789-7000"
            onChange={(e) => {
              const vat = e.target.value;
              const next = { ...draft, vatRegistrationNo: vat };
              // Auto-derive TIN from VAT prefix (only when TIN is empty).
              const derived = vatToTin(vat);
              if (derived && !((draft.tin || "").trim())) next.tin = derived;
              setDraft(next);
            }}
          />
          <p style={{ color: "var(--ink-4)", fontSize: 11, margin: "4px 2px 0" }}>
            Format: <code>&lt;TIN&gt;-7000</code> · we'll auto-fill the TIN below.
          </p>
        </div>
        <div className="form-field">
          <label className="form-label">
            TIN
            {(draft.vatRegistrationNo || "").trim() && !(draft.tin || "").trim() && (
              <span style={{ color: "var(--danger, #b91c1c)", marginLeft: 6, fontWeight: 600 }}>· required (VAT entered)</span>
            )}
          </label>
          <input
            className="form-input"
            value={draft.tin ?? ""}
            onChange={(e) => setDraft({ ...draft, tin: e.target.value })}
            style={{
              borderColor: (draft.vatRegistrationNo || "").trim() && !(draft.tin || "").trim()
                ? "var(--danger, #b91c1c)" : undefined,
            }}
          />
        </div>
        <div className="form-field">
          <label
            className="form-label"
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={draft.isExport ?? false}
              onChange={(e) => setDraft({ ...draft, isExport: e.target.checked })}
            />
            {t("customer.isExport", lang)}
          </label>
          <div style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 4 }}>
            {t("customer.isExport.hint", lang)}
          </div>
        </div>
        {err && <div className="banner banner-error">{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1, justifyContent: "center" }}>
            {saving ? "Saving…" : editing.id ? "Save changes" : "Add customer"}
          </button>
          {editing && editing.id && (
            <button
              type="button"
              className="btn btn-line"
              onClick={async () => {
                if (editing && confirm(`Remove ${editing.name}?`)) {
                  await customersApi.remove(editing.id);
                  onSaved();
                }
              }}
            >
              Delete
            </button>
          )}
        </div>

        {editing && editing.id && (
          <>
            <div id="statement"><StatementDownloader customerId={editing.id} customerName={editing.name} /></div>
            <div id="outstanding"><OutstandingInvoicesPanel customerId={editing.id} /></div>
          </>
        )}
      </form>
    </div>
  );
}

function StatementDownloader({ customerId, customerName }: { customerId: string; customerName: string }) {
  const today = todayLocalISO();
  const jan1 = `${new Date().getFullYear()}-01-01`;
  const [from, setFrom] = useState(jan1);
  const [to, setTo] = useState(today);
  const safeName = customerName.replace(/[^a-z0-9]+/gi, "-");
  return (
    <div style={{ marginTop: 14, padding: 14, background: "var(--paper-2)", borderRadius: 8 }}>
      <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 14, marginBottom: 8 }}>
        Statement of account
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <div className="form-field">
          <label className="form-label" style={{ fontSize: 11 }}>From</label>
          <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: 12, padding: "6px 8px" }} />
        </div>
        <div className="form-field">
          <label className="form-label" style={{ fontSize: 11 }}>To</label>
          <input type="date" className="form-input" value={to} onChange={(e) => setTo(e.target.value)} style={{ fontSize: 12, padding: "6px 8px" }} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          className="btn btn-line"
          style={{ flex: 1, justifyContent: "center", fontSize: 12 }}
          onClick={() => viewFileInNewTab(`/customers/${customerId}/statement.pdf?from=${from}&to=${to}`)}
        >
          PDF
        </button>
        <button
          className="btn btn-line"
          style={{ flex: 1, justifyContent: "center", fontSize: 12 }}
          onClick={() => downloadFile(`/customers/${customerId}/statement.csv?from=${from}&to=${to}`, `${safeName}-statement-${from}-to-${to}.csv`)}
        >
          CSV
        </button>
      </div>
    </div>
  );
}

// Outstanding invoices for the focused customer — what they still owe us.
// Surfaces in the side panel so when Shamil opens a customer he can see at
// a glance "this customer owes LKR X across 3 invoices". Each row links
// straight to the invoice detail page.
function OutstandingInvoicesPanel({ customerId }: { customerId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["customer-outstanding", customerId],
    queryFn: () => invoicesApi.list("outstanding", customerId),
    staleTime: 30_000,
  });
  const rows = data?.invoices ?? [];
  const outstandingCents = rows.reduce(
    (s, r) => s + (Number(r.total_cents) - Number(r.paid_cents)),
    0,
  );
  const fmt = (cents: number) =>
    `LKR ${(cents / 100).toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div style={{ marginTop: 14, padding: 14, background: "var(--paper-2)", borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 14 }}>
          Outstanding invoices
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: outstandingCents > 0 ? "#b91c1c" : "var(--ink-3)" }}>
          {fmt(outstandingCents)}
        </div>
      </div>
      {isLoading ? (
        <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--ink-3)" }}>None — all invoices are paid or voided.</div>
      ) : (
        <table style={{ width: "100%", fontSize: 12 }}>
          <tbody>
            {rows.map((r) => {
              const balance = Number(r.total_cents) - Number(r.paid_cents);
              const overdue = new Date(r.due_date) < new Date() && r.status !== "paid";
              return (
                <tr key={r.id} style={{ borderTop: "0.5px solid var(--line)" }}>
                  <td style={{ padding: "6px 0" }}>
                    <a href={`/clearbooks/invoices/${r.id}`} style={{ fontWeight: 500 }}>
                      {r.invoice_number}
                    </a>
                    <div style={{ fontSize: 10, color: overdue ? "#b91c1c" : "var(--ink-4)" }}>
                      Due {r.due_date}{overdue ? " · overdue" : ""}
                    </div>
                  </td>
                  <td style={{ padding: "6px 0", textAlign: "right" }}>
                    <div>{fmt(balance)}</div>
                    <div style={{ fontSize: 10, color: "var(--ink-4)" }}>
                      of {fmt(Number(r.total_cents))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div className="form-field">
      <label className="form-label">{label}</label>
      <input
        className="form-input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
    </div>
  );
}
